export const GRAPH_COLORS = ["#1a73e8", "#e8561a", "#0d9f6e", "#9333ea", "#e8a81a"];

export const CATEGORIES = ["GPS", "Testing", "Forceplate", "Nutrition", "Cognition", "Wellness"] as const;

export type { MetricOption, SubjectEntity, ChartSortOption } from "../Processing/types";

export const AGG_OPTIONS = [
  { value: "avg", label: "Average" },
  { value: "best", label: "Best" },
  { value: "sum", label: "Sum" },
] as const;

export const METRIC_TYPE_OPTIONS = [
  { value: "metric", label: "Metric" },
  { value: "acwr", label: "ACWR" },
] as const;

export const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "last1week", label: "Last 1 week" },
  { value: "last2weeks", label: "Last 2 weeks" },
  { value: "last4weeks", label: "Last 4 weeks" },
  { value: "last3months", label: "Last 3 months" },
] as const;

/**
 * Presets that resolve to a window the DATA defines rather than a calendar span. They
 * are shortcuts for the two counts coaches ask for most; Advanced date filters takes
 * any other count.
 *
 * Offered where `latest_n` is legal (see DateRangeForm's `allowLatestN`), which now
 * includes the slicer: the backend reads these preset strings in
 * CONTROLLER_TRAINING_COUNT_PRESETS and builds the same window from them.
 */
export const TRAINING_COUNT_RANGES = [
  { value: "last5trainings", label: "Last 5 Trainings", n: 5 },
  { value: "last10trainings", label: "Last 10 Trainings", n: 10 },
] as const;

/** The count a training-count preset resolves to, or null when it is a calendar preset. */
export function trainingCountForPreset(preset?: string): number | null {
  return TRAINING_COUNT_RANGES.find((r) => r.value === preset)?.n ?? null;
}

/**
 * Widget types the backend will not substitute a session window into: it declines, and
 * they keep their own date range. Mirrors LATEST_N_UNSUPPORTED_WIDGETS in
 * api/Sandbox/schema.py, and the value is the reason a coach reads.
 *
 * A body map is here for completeness. It binds through `single_date`, which takes the
 * range's END — always a calendar date — so it follows a windowed slicer correctly.
 */
export const SESSION_WINDOW_DECLINED: Record<string, string> = {
  stat_tile: "a stat tile already shows the latest value against the last N through its own comparison",
  player_notes: "notes are filed against a timestamp and carry no metric to count sessions by",
  body_map: "a body map reads one date, and a session window is a range",
};

/** Why this widget type declines a session window, or undefined when it takes one. */
export function declinesSessionWindow(widgetType?: string): string | undefined {
  return widgetType ? SESSION_WINDOW_DECLINED[widgetType] : undefined;
}

/** The count a new session window starts on. */
export const DEFAULT_LATEST_N = 5;
/** Bounds on the count, matching the backend's LATEST_N_MAX_COUNT. 90 is the hard limit,
 *  not a UI courtesy: a larger count fails validation on save. */
export const LATEST_N_MIN = 1;
export const LATEST_N_MAX = 90;

/**
 * Whether these date fields describe a session window rather than a calendar one.
 *
 * Mirrors the backend's ControllerFormState.to_date_spec: the custom range decides while
 * it is open, and the preset decides while it is closed. Reading startDateType alone
 * called a slicer windowed when it held a stale type behind a closed panel, and the
 * backend was serving the preset.
 */
export function usesSessionWindow(fields?: {
  showAdvancedDate?: boolean;
  startDateType?: string;
  dateRange?: string;
}): boolean {
  if (fields?.showAdvancedDate) return fields.startDateType === "latest_n";
  return trainingCountForPreset(fields?.dateRange) != null;
}

export const WEEKDAY_TAGS = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
] as const;

/**
 * The "no day filter" choice, shown as the first option in the day-filter dropdown.
 *
 * It is a DISPLAY value only and is never stored: the backend's DayTag enum holds the
 * fourteen real tags and nothing else, so storing this would fail validation. An empty
 * `dayFilter` already means "every date with data", which is what Trainings names.
 */
export const ALL_TRAININGS_OPTION = "__all_trainings__";

export const GAMEDAY_TAGS = [
  { value: "gd-3", label: "GD\u22123" },
  { value: "gd-2", label: "GD\u22122" },
  { value: "gd-1", label: "GD\u22121" },
  { value: "gd", label: "GD" },
  { value: "gd+1", label: "GD+1" },
  { value: "gd+2", label: "GD+2" },
  { value: "gd+3", label: "GD+3" },
] as const;

export const SUBJECT_OPTIONS = [
  { value: "player", label: "Player" },
  { value: "position", label: "Position" },
  { value: "drill", label: "Drill" },
  { value: "team", label: "Team" },
] as const;

export const GROUPBY_TIME_GRAINS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
] as const;

export const GROUPBY_ENTITY_OPTIONS = [
  { value: "player", label: "Player" },
  { value: "position", label: "Position" },
  { value: "drill", label: "Drill" },
] as const;

export const BENCHMARK_TYPES = [
  { value: "teamAvg", label: "Average Line" },
  { value: "athleteBaseline", label: "Athlete Baseline" },
  { value: "custom", label: "Custom" },
] as const;

export type GroupByOption = "day" | "week" | "month" | "year" | "player" | "position" | "drill";

export const GROUPBY_OPTIONS: { value: GroupByOption; label: string; section: "time" | "entity" }[] = [
  { value: "day", label: "Day", section: "time" },
  { value: "week", label: "Week", section: "time" },
  { value: "month", label: "Month", section: "time" },
  { value: "year", label: "Year", section: "time" },
  { value: "player", label: "Player", section: "entity" },
  { value: "position", label: "Position", section: "entity" },
  { value: "drill", label: "Drill", section: "entity" },
];

import type { ChartSortOption } from "../Processing/types";

type SortKind = "alpha" | "value" | "position";

const SORT_OPTION_KINDS: { value: ChartSortOption; kind: SortKind }[] = [
  { value: "alpha-asc", kind: "alpha" },
  { value: "alpha-desc", kind: "alpha" },
  { value: "value-asc", kind: "value" },
  { value: "value-desc", kind: "value" },
  { value: "position-asc", kind: "position" },
  { value: "position-desc", kind: "position" },
];

// "alpha" sorts only make sense as standalone options for player/drill — for
// position groupBy the equivalent is "position-*" with the same labelling.
const ALPHA_GROUPBYS: GroupByOption[] = ["player", "drill"];
const POSITION_GROUPBYS: GroupByOption[] = ["player", "position"];

export function sortOptionAllowed(option: ChartSortOption, groupBy: GroupByOption): boolean {
  const meta = SORT_OPTION_KINDS.find((o) => o.value === option);
  if (!meta) return false;
  if (meta.kind === "value") return true;
  if (meta.kind === "alpha") return ALPHA_GROUPBYS.includes(groupBy);
  return POSITION_GROUPBYS.includes(groupBy);
}

export interface SortOptionDisplay {
  value: ChartSortOption;
  kind: SortKind;
  label: string;       // long label shown in the dropdown
  shortLabel: string;  // short label shown on the bar button
}

export function describeSortOption(
  option: ChartSortOption,
  groupBy: GroupByOption,
): SortOptionDisplay {
  const meta = SORT_OPTION_KINDS.find((o) => o.value === option) ?? SORT_OPTION_KINDS[2];
  const ascending = option.endsWith("-asc");

  if (meta.kind === "value") {
    return {
      value: option,
      kind: "value",
      shortLabel: "Value",
      label: ascending ? "Value (0-9)" : "Value (9-0)",
    };
  }
  if (meta.kind === "position") {
    return {
      value: option,
      kind: "position",
      shortLabel: "Player Position",
      label: ascending ? "Player Position (A-Z)" : "Player Position (Z-A)",
    };
  }
  // alpha-*  — labelled by what the categories actually are for this groupBy
  const short = groupBy === "drill" ? "Drill Name" : "Player Name";
  return {
    value: option,
    kind: "alpha",
    shortLabel: short,
    label: ascending ? `${short} (A-Z)` : `${short} (Z-A)`,
  };
}

export function getAvailableSortOptions(groupBy: GroupByOption): SortOptionDisplay[] {
  return SORT_OPTION_KINDS
    .filter((o) => sortOptionAllowed(o.value, groupBy))
    .map((o) => describeSortOption(o.value, groupBy));
}

export const DEFAULT_SORT_BY: ChartSortOption = "value-desc";

export interface SortKindDisplay {
  kind: SortKind;
  label: string;
  ascValue: ChartSortOption;
  descValue: ChartSortOption;
}

export function getAvailableSortKinds(groupBy: GroupByOption): SortKindDisplay[] {
  const seen = new Set<SortKind>();
  const result: SortKindDisplay[] = [];
  for (const opt of SORT_OPTION_KINDS) {
    if (sortOptionAllowed(opt.value, groupBy) && !seen.has(opt.kind)) {
      seen.add(opt.kind);
      const label =
        opt.kind === "value" ? "Value"
        : opt.kind === "position" ? "Position"
        : groupBy === "drill" ? "Drill"
        : "Player";
      result.push({
        kind: opt.kind,
        label,
        ascValue: `${opt.kind}-asc` as ChartSortOption,
        descValue: `${opt.kind}-desc` as ChartSortOption,
      });
    }
  }
  return result;
}

